// Rate-limiting/document-sharing audit (2026-09-14): briefly raised to
// 50MB, then reverted per explicit product decision -- 20MB already
// covers the overwhelming majority of real uploads, and the Supabase
// Storage buckets themselves were deliberately kept at 20MB too. Matches
// backend/config/file-limits.js.
export const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20MB
// 100KB (2026-09-20): product spec caps the logo at 100KB so it loads fast
// on every page. Keep the companies-logos bucket's own file_size_limit in
// Supabase Storage in sync (see backend/migrations for the matching SQL),
// or an upload under the old higher limit will pass this check and still
// get rejected at the storage layer.
export const MAX_LOGO_FILE_BYTES = 100 * 1024;
// The only raster/vector formats accepted for a company logo -- GIF (no
// value for a static logo) and camera formats like HEIC (inconsistent
// browser support) are deliberately excluded.
export const ALLOWED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
// SVG is vector -- it has no fixed raster resolution to check.
export const RASTER_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const MAX_LOGO_DIMENSION_PX = 400;
// CompanyTab.tsx's upload/delete flow and the sidebar's CompanyLogoBox.tsx
// live in entirely separate, already-mounted parts of the component tree
// (Settings page vs. the app shell), so a plain prop/state change can't
// tell one about the other. CompanyTab dispatches this on window after a
// successful upload/delete; CompanyLogoBox listens for it and refetches.
export const LOGO_UPDATED_EVENT = 'company-logo-updated';

/**
 * Human-readable file size for user-facing copy (e.g. "100KB", "2MB").
 * `MAX_LOGO_FILE_BYTES` used to be reported as `${bytes/1024/1024}MB`
 * rounded to a whole number, which silently printed "0MB" once the limit
 * dropped below 1MB -- this covers both ranges correctly.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(0)}MB`;
}

/** Resolves to a raster image's pixel width/height by decoding it in-browser. */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image dimensions'));
    };
    img.src = url;
  });
}
